import { parseEnvelope, RealtimeEnvelope } from "./envelope";

/**
 * API Gateway drops a WebSocket connection that has carried no frame for ten
 * minutes. Half that leaves room for a ping to be late.
 */
export const PING_INTERVAL_MS = 5 * 60_000;

/** API Gateway's hard cap on one WebSocket connection's life. */
export const MAX_CONNECTION_MS = 2 * 60 * 60_000;

/**
 * How long before the connection's deadline a new one is opened.
 *
 * The backend stops sending to a connection once its token has expired - the
 * socket stays open but goes quiet - so the client has to move to a new
 * connection before then, not after. Inside auth0-spa-js's 60s renewal leeway,
 * so the token read for the new ticket is a fresh one rather than the one that
 * is about to lapse.
 */
export const RENEW_BEFORE_MS = 45_000;

/** Never renew sooner than this, however close the deadline already is. */
export const MIN_RENEW_DELAY_MS = 5_000;

export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_MAX_MS = 60_000;

/**
 * A connection that stayed up this long counts as healthy, so the next failure
 * starts the backoff over. One that the server closes straight after opening
 * keeps backing off instead of reconnecting every second.
 */
export const STABLE_CONNECTION_MS = 30_000;

export interface RealtimeSocketOptions {
  /** `ws(s)://host/stage`, without the ticket. */
  url: string;
  /** A one-time ticket; rejects when one could not be minted. */
  mintTicket: () => Promise<string>;
  /** When the access token the ticket is minted from expires, in ms. */
  readTokenExpiry: () => Promise<number | undefined>;
  onEvent: (envelope: RealtimeEnvelope) => void;
  /** Every successful open, including each reconnect. */
  onOpen: () => void;
  onConnectedChange: (connected: boolean) => void;
  /** For tests. */
  createSocket?: (url: string) => WebSocket;
  random?: () => number;
}

/**
 * One realtime connection, kept open: ticket, connect, ping, reconnect with
 * jittered backoff, and move to a new connection before the current one's
 * deadline. Knows nothing about what the events mean.
 */
export class RealtimeSocket {
  private socket?: WebSocket;
  private stopped = true;
  // Bumped by every connect and stop, so a ticket that resolves after the
  // world has moved on - or a socket that closes after being replaced - can
  // tell it is stale and do nothing.
  private generation = 0;
  private attempt = 0;
  private openedAt = 0;
  private connected = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private renewTimer?: ReturnType<typeof setTimeout>;
  private pingTimer?: ReturnType<typeof setInterval>;

  constructor(private readonly options: RealtimeSocketOptions) {}

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    this.connect();
  }

  stop() {
    this.stopped = true;
    this.generation++;
    this.teardown();
  }

  /**
   * Drop the current connection, if any, and open a new one now: a new
   * organization, the network coming back. Starts the backoff over.
   */
  reconnect() {
    if (this.stopped) return;
    this.attempt = 0;
    this.connect();
  }

  /** Whether a connection is open right now. */
  get open() {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private teardown() {
    clearTimeout(this.retryTimer);
    clearTimeout(this.renewTimer);
    clearInterval(this.pingTimer);
    const socket = this.socket;
    this.socket = undefined;
    if (socket) {
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      socket.close();
    }
    this.setConnected(false);
  }

  private setConnected(connected: boolean) {
    if (this.connected === connected) return;
    this.connected = connected;
    this.options.onConnectedChange(connected);
  }

  private async connect() {
    this.teardown();
    const generation = ++this.generation;

    let ticket: string;
    let tokenExpiry: number | undefined;
    try {
      // The expiry first: if the token is renewed between the two, the ticket
      // carries the later expiry and this one errs early, which is safe.
      tokenExpiry = await this.options.readTokenExpiry();
      ticket = await this.options.mintTicket();
    } catch (err) {
      if (generation === this.generation) this.scheduleRetry();
      return;
    }
    if (generation !== this.generation) return;

    const separator = this.options.url.includes("?") ? "&" : "?";
    const url = `${this.options.url}${separator}ticket=${encodeURIComponent(ticket)}`;
    let socket: WebSocket;
    try {
      socket = this.options.createSocket
        ? this.options.createSocket(url)
        : new WebSocket(url);
    } catch (err) {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.openedAt = Date.now();
      this.setConnected(true);
      this.pingTimer = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ action: "ping" }));
        }
      }, PING_INTERVAL_MS);
      this.scheduleRenewal(tokenExpiry);
      this.options.onOpen();
    };

    socket.onmessage = (message: MessageEvent) => {
      const envelope = parseEnvelope(message.data);
      if (envelope) this.options.onEvent(envelope);
    };

    socket.onclose = () => {
      if (generation !== this.generation) return;
      const wasStable =
        this.openedAt > 0 && Date.now() - this.openedAt >= STABLE_CONNECTION_MS;
      this.openedAt = 0;
      this.socket = undefined;
      clearInterval(this.pingTimer);
      clearTimeout(this.renewTimer);
      this.setConnected(false);
      if (wasStable) this.attempt = 0;
      this.scheduleRetry();
    };
  }

  /**
   * The connection is good until the earlier of the token's expiry and API
   * Gateway's two-hour cap, counted from now; the backend applies the same
   * rule. Without a readable expiry, the cap alone.
   */
  private scheduleRenewal(tokenExpiry: number | undefined) {
    const now = Date.now();
    const deadline = Math.min(
      tokenExpiry && Number.isFinite(tokenExpiry) ? tokenExpiry : Infinity,
      now + MAX_CONNECTION_MS,
    );
    const delay = Math.max(
      deadline - RENEW_BEFORE_MS - now,
      MIN_RENEW_DELAY_MS,
    );
    this.renewTimer = setTimeout(() => this.reconnect(), delay);
  }

  /** "Equal jitter": at least half the capped exponential delay, at most all of it. */
  private scheduleRetry() {
    if (this.stopped) return;
    const cap = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempt);
    const random = this.options.random ?? Math.random;
    const delay = cap / 2 + random() * (cap / 2);
    this.attempt++;
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }
}
